import { tz } from '@date-fns/tz';
import { addDays, differenceInHours, format, parse, parseISO } from 'date-fns';

/**
 * Replaces the time portion of an existing UTC ISO timestamp with a user-supplied
 * `HH:mm` value, keeping the calendar date of the reference in the station's timezone.
 *
 * **Why this exists:** a `<input type="time">` gives only hours and minutes — no date,
 * no timezone. To build a full UTC ISO for the mutation, the date is borrowed from
 * `referenceIso` (the station's planned/actual timestamp), converted to the local
 * calendar date in `timezone`, and then the new time is applied.
 *
 * **Midnight crossing:** if the new time is more than 12 hours before the reference
 * (e.g. arrival `01:00` when departure was `23:00`), the date is advanced by one day
 * to handle overnight trips correctly.
 *
 * DST transitions are handled automatically by `@date-fns/tz` — no manual offset
 * arithmetic needed.
 *
 * @param {object} params
 * @param {string} params.referenceIso
 *   Existing UTC ISO timestamp used as the date anchor.
 *   Example: `'2026-08-06T21:00:00.000Z'`
 * @param {string} params.newTime
 *   User-typed time in `HH:mm` format.
 *   Example: `'14:30'`
 * @param {string} params.timezone
 *   IANA timezone identifier for the station.
 *   Example: `'Europe/Berlin'`
 * @returns {string}
 *   UTC ISO string with the new time applied.
 *   Example: `'2026-08-06T12:30:00.000Z'` (14:30 Berlin time = 12:30 UTC in summer)
 */
export const replaceTimeInTimestamp = ({ referenceIso, newTime, timezone }) => {
	const tzContext = tz(timezone);
	const refDate = parseISO(referenceIso);

	const localDate = format(refDate, 'yyyy-MM-dd', { in: tzContext });
	let candidate = parse(`${localDate} ${newTime}`, 'yyyy-MM-dd HH:mm', refDate, { in: tzContext });

	if (differenceInHours(candidate, refDate) < -12) {
		candidate = addDays(candidate, 1);
	}

	// TZDate.toISOString() returns local time with offset (e.g. "+02:00"), not UTC.
	// Re-format in explicit UTC context; XXX renders as "Z" when offset is zero.
	return format(candidate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX", { in: tz('UTC') });
};
